import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';
import TransitionRedirect from './TransitionRedirect';
import AppLaunch from './AppLaunch';
import VuCard from './VuCard';
import ClientAnalytics from '../analytics/ClientAnalytics';
import settings from '../../config/settings';
import _ from 'lodash';
import Cookies from 'js-cookie';
import * as RedirectConst from '../const/RedirectConst';
import DestinationTypes from '../const/DestinationTypes';
import * as RedirectPreferenceUtils from '../utils/RedirectPreferenceUtils';
import {getDeviceType, isMobile} from '../utils/BrowserUtils';
import {injectHtml} from '../utils/DomUtils';
import {ImagePropType} from '../utils/ApiPropTypes';
import {getFingerprints} from '../fingerprint/FingerprintUtils';
import RedirectPreferenceApi from '../api/RedirectPreferenceApi';
import {getCachedPreference, setCachedPreference} from '../fingerprint/FingerprintRedirectPreferenceCache';
import {captureException} from '../utils/SentryUtils';

export default class TransitionRedirectWrapper extends React.Component {
  constructor() {
    super();
    this.state = {
      isRedirectCanceled: false,
      hasTimeoutPassed: false
    };
  }

  componentDidMount() {
    this.appendDestinationRedirectTrackingCode();

    getFingerprints((fingerprintData) => {
      this.fingerprintData = fingerprintData;
    });
    window.addEventListener('load', this.onPageLoad);
  }

  componentWillUnmount() {
    this.clearRedirectTimeout();
    window.removeEventListener('load', this.onPageLoad);
  }

  @autobind
  setupRedirectTimeout() {
    const cfgTimeoutLength = (_.get(settings, 'widget.redirectTimeoutMS') || 5000);
    this.timeout = setTimeout(() => {
      this.onRedirectTimeout();
    }, cfgTimeoutLength);
  }

  isAppLaunchMode() {
    return !!this.props.appDestination && isMobile.any();
  }

  async onRedirectTimeout() {
    const {destination, campaignId} = this.props;

    if (this.isAppLaunchMode()) {
      this.trackRedirection(RedirectConst.redirectActions.appLaunchView);
    } else {
      try {
        this.setRedirectPreference(campaignId, destination.name, destination.kind);
        await this.trackRedirection(RedirectConst.redirectActions.auto, destination.url);
      } finally {
        this.redirect(destination.url);
      }
    }
    this.setState({hasTimeoutPassed: true});
  }

  hasDestinationTrackingCodes() {
    const {destination} = this.props;
    return !!destination.trackingCode;
  }

  appendDestinationRedirectTrackingCode() {
    if (this.destinationTrackingCodeContainer) {
      const {destination} = this.props;
      if (destination.trackingCode) {
        const wrapper = document.createElement('span');
        this.destinationTrackingCodeContainer.appendChild(wrapper);
        injectHtml(wrapper, destination.trackingCode);
      }
    }
  }

  @autobind
  onAppLaunchClick() {
    const {destination, campaignId} = this.props;
    this.trackRedirection(RedirectConst.redirectActions.appLaunchClick);
    this.setRedirectPreference(campaignId, destination.name, destination.kind);
  }

  @autobind
  onAppDownloadClick(downloadUrl) {
    const {destination, campaignId} = this.props;
    this.trackRedirection(RedirectConst.redirectActions.appDownloadClick, downloadUrl);
    this.setRedirectPreference(campaignId, destination.name, destination.kind);
  }

  async trackRedirection(userAction, url) {
    const {redirectType, destination, cardId, campaignId} = this.props;

    try {
      await ClientAnalytics.sendCardRedirect(
        cardId,
        campaignId,
        userAction,
        redirectType,
        url || destination.url,
        getDeviceType(),
        destination.name,
      );
      if (userAction === RedirectConst.redirectActions.auto) {
        this.isAutoRedirectTracked = true;
      }
    } catch (e) {
      if (userAction === RedirectConst.redirectActions.auto) {
        this.isAutoRedirectTracked = false;
      }
    }
  }

  async setRedirectPreference(campaignId, destinationName, destinationType) {
    const cookie = Cookies.getJSON(RedirectConst.cookieName);
    const updated = RedirectPreferenceUtils.setDestination(cookie, campaignId, destinationName, destinationType);
    Cookies.set(RedirectConst.cookieName, updated);

    if (this.isFingerprintRedirectActive()) {
      const preference = getCachedPreference() || RedirectPreferenceUtils.getEmpty();
      const updated = RedirectPreferenceUtils.setDestination(preference, campaignId, destinationName, destinationType);
      if (this.fingerprintData) {
        try {
          await RedirectPreferenceApi.set(this.fingerprintData, updated);
          setCachedPreference(preference);
        } catch (e) {
          captureException(e);
        }
      }
    }
  }

  isFingerprintRedirectActive() {
    return !_.get(this.props, 'cardProps.campaign.isFingerprintRedirectDisabled');
  }

  async removeDestinationFromPreferences() {
    const {destination, campaignId} = this.props;
    const cookie = Cookies.getJSON(RedirectConst.cookieName);
    const updated = RedirectPreferenceUtils.removeDestination(cookie, campaignId, destination.name, destination.kind);
    Cookies.set(RedirectConst.cookieName, updated);

    if (this.isFingerprintRedirectActive()) {
      const preference = getCachedPreference() || RedirectPreferenceUtils.getEmpty();
      const updated = RedirectPreferenceUtils.removeDestination(preference, campaignId, destination.name, destination.kind);
      if (this.fingerprintData) {
        try {
          await RedirectPreferenceApi.set(this.fingerprintData, updated);
          setCachedPreference(preference);
        } catch (e) {
          captureException(e);
        }
      }
    }
  }

  /* Wrapper for 'window.location.replace' to make mocking / testing easier */
  redirect(redirectUrl) {
    window.location.replace(redirectUrl);
  }

  render() {
    const {isRedirectCanceled, hasTimeoutPassed} = this.state;

    if (isRedirectCanceled) {
      return (<VuCard {...this.props.cardProps} isChangeProviderRequested={true}/>);
    }

    const {
      network, destination, appDestination,
      transitionOnText, transitionToText, transitionCancelButtonText, transitionCancelButtonColor,
      transitionBackgroundImage, transitionBackgroundVideo
    } = this.props;
    return (
      <div style={{height: '100%'}}>
        {
          (hasTimeoutPassed && this.isAppLaunchMode()) ?
            <AppLaunch destination={destination}
                       network={network}
                       appDestination={appDestination}
                       onLaunchClick={this.onAppLaunchClick}
                       onDownloadClick={this.onAppDownloadClick}
                       onCancel={this.onRedirectCancel}/>
            :
            <TransitionRedirect destination={destination}
                      network={network}
                      transitionToText={transitionToText}
                      transitionOnText={transitionOnText}
                      cancelButtonText={transitionCancelButtonText}
                      cancelButtonColor={transitionCancelButtonColor}
                      backgroundImage={transitionBackgroundImage}
                      backgroundVideo={transitionBackgroundVideo}
                      onCancel={this.onRedirectCancel}
            />
        }
        {
          this.renderDestinationClickTrackingCodeContainer()
        }
      </div>
    );
  }

  renderDestinationClickTrackingCodeContainer() {
    return (
      <span className="redirectWrapper__destinationTrackingCode"
            style={{position: 'absolute', bottom: '0'}}
            ref={(node) => {
              this.destinationTrackingCodeContainer = node;
            }}
      />
    );
  }

  @autobind
  onPageLoad() {
    const {cardId, campaignId, destination, cardProps: {record, collectionId, rendererId, showsPageId}} = this.props;
    ClientAnalytics.sendCardRedirectTransitionViewed(
      '',
      _.get(record, 'program.id', ''),
      _.get(record, 'title', ''),
      rendererId,
      cardId,
      campaignId,
      collectionId,
      showsPageId,
      destination.name
    );
    this.setupRedirectTimeout();
  }

  @autobind
  onRedirectCancel() {
    if (!this.isAutoRedirectTracked) {
      this.clearRedirectTimeout();
      this.clearTrackingCodeTimeout();
      this.removeDestinationFromPreferences();
      this.setState({isRedirectCanceled: true});
      this.trackRedirection(RedirectConst.redirectActions.cancel);
    }
  }

  @autobind
  clearRedirectTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  @autobind
  clearTrackingCodeTimeout() {
    if (this.trackingCodeTimeout) {
      clearTimeout(this.trackingCodeTimeout);
    }
  }
}

TransitionRedirectWrapper.propTypes = {
  redirectType: PropTypes.string.isRequired,
  cardId: PropTypes.number.isRequired,
  campaignId: PropTypes.number,
  network: PropTypes.shape({
    name: PropTypes.string.isRequired,
    transitionImageUrl: PropTypes.string.isRequired,
  }),
  transitionToText: PropTypes.string,
  transitionOnText: PropTypes.string,
  transitionCancelButtonText: PropTypes.string,
  transitionCancelButtonColor: PropTypes.string,
  transitionBackgroundImage: ImagePropType,
  transitionBackgroundVideo: PropTypes.string,
  destination: PropTypes.shape({
    url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    transitionImageUrl: PropTypes.string.isRequired,
    kind: PropTypes.oneOf([
      DestinationTypes.customDestination,
      DestinationTypes.network,
      DestinationTypes.provider
    ]).isRequired,
    trackingCode: PropTypes.string.isRequired,
  }).isRequired,
  appDestination: PropTypes.shape({
    name: PropTypes.string,
    providerLogo: ImagePropType.isRequired,
    useApp: PropTypes.bool.isRequired,
    locations: PropTypes.arrayOf(
      PropTypes.shape({
        store: PropTypes.string.isRequired,
        appScreenImage: ImagePropType,
        appDownloadUrl: PropTypes.string
      }).isRequired
    ).isRequired
  }),
  redirectPreference: PropTypes.object,
  cardProps: PropTypes.object.isRequired
};

TransitionRedirectWrapper.defaultProps = {};
